import { useEffect, useState, useCallback, useMemo } from 'react'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { v4 as uuid } from 'uuid'
import {
  ReservationGroupUpsertGridRowGroupingInfo,
  ReservationGroupUpsertGridRow,
  ReservationGroupUpsertGridRowOccupancy,
  ReservationGroupUpsertGridRowType,
  ReservationGroupUpsertGridTotalRow,
  ResourceTypePerOccupancy,
  ReservationRatesPerOccupancy,
} from '../reservation-group.types'
import { LocalizedString } from 'hic-components'
import { useTenant } from 'src/app/hooks/context/context.hook'

export type ChildrenAgeRanges =
  | {
      percentage: number
      child: {
        id: string
        taxonomy: {
          description: LocalizedString
        }
        noOccupancy: boolean
      }
    }[]
  | null

export default function useReservationGroupUpsertRows(
  start: Date,
  end: Date,
  nights: number,
  propertyId: string,
  hideChildren: boolean,
  childrenAgeRange: ChildrenAgeRanges,
  reservationRates: ReservationRatesPerOccupancy,
  resourceTypePerOccupancy: ResourceTypePerOccupancy,
) {
  const { id: tenantId } = useTenant()
  const localizeString = useLocalizeString()
  const [rows, setRows] = useState<Map<ReservationGroupUpsertGridRowGroupingInfo, ReservationGroupUpsertGridRow[]>>(new Map())

  const dataGridRows = useMemo(() => {
    const tmpRows = Array.from(rows.values()).flat()
    const dgRows: (ReservationGroupUpsertGridTotalRow | ReservationGroupUpsertGridRow)[] = rows?.values()
      ? tmpRows.sort((a, b) => {
          if (a.type.maxOccupancy < b.type.maxOccupancy) return -1
          else if (a.type.maxOccupancy > b.type.maxOccupancy) return 1
          else {
            if (a.type.resourceTypeId < b.type.resourceTypeId) return -1
            else if (a.type.resourceTypeId > b.type.resourceTypeId) return 1
          }
          return 0
        })
      : []

    const totalChildren = tmpRows
      .filter((row) => row.resourceNumber > 0)
      .reduce((prev: { description: string; quantity: number }[], curr) => {
        return curr.occupancies.children.map((child) => {
          const prevQuantity = prev.find((v) => v.description === child.label)?.quantity ?? 0
          return {
            description: child.label,
            quantity: child.quantity * curr.resourceNumber + prevQuantity,
          }
        })
      }, [])

    const summaryRowInitialAvgPrice = tmpRows.filter((row) => row.resourceNumber > 0).reduce((prev, curr) => prev + curr.initialAveragePrice * curr.resourceNumber, 0)
    const summaryRowAvgPrice = tmpRows.filter((row) => row.resourceNumber > 0).reduce((prev, curr) => prev + curr.averagePrice * curr.resourceNumber, 0)

    const summaryRowInitialTotalPrice = tmpRows.filter((row) => row.resourceNumber > 0).reduce((prev, curr) => prev + curr.initialTotalPrice * curr.resourceNumber, 0)
    const summaryRowTotalPrice = tmpRows.filter((row) => row.resourceNumber > 0).reduce((prev, curr) => prev + curr.totalPrice * curr.resourceNumber, 0)

    const totalResourceNumber = tmpRows.reduce((prev, curr) => prev + curr.resourceNumber, 0)
    const summaryRow: ReservationGroupUpsertGridTotalRow = {
      id: uuid(),
      resources: {
        resourceNumber: totalResourceNumber,
        occupancy: tmpRows.reduce((prev, curr) => prev + curr.type.occupancy * curr.resourceNumber, 0),
        maxOccupancy: tmpRows.reduce((prev, curr) => prev + curr.type.maxOccupancy * curr.resourceNumber, 0),
      },
      occupancy: {
        adults: tmpRows.filter((row) => row.resourceNumber > 0).reduce((prev, curr) => prev + curr.occupancies.adults * curr.resourceNumber, 0),
        children: totalChildren,
      },
      averagePrice: summaryRowAvgPrice,
      initialAveragePrice: summaryRowInitialAvgPrice,
      initialTotalPrice: summaryRowInitialTotalPrice,
      totalPrice: summaryRowTotalPrice,
      nights: nights,
      disablePrices: totalResourceNumber <= 0,
      position: 999999,
    }

    dgRows.push(summaryRow)
    return dgRows
  }, [rows])

  const sAddRow = useCallback(
    (key: ReservationGroupUpsertGridRowGroupingInfo, row: ReservationGroupUpsertGridRow) => {
      const tmpRowsMap = new Map(rows)
      const newRows = tmpRowsMap.get(key) ?? []
      newRows.push(row)
      rows.set(key, newRows)
      setRows(tmpRowsMap)
    },
    [rows],
  )

  const sRemoveRow = useCallback(
    (key: ReservationGroupUpsertGridRowGroupingInfo, rowId: string) => {
      const tmpRowsMap = new Map(rows)
      const newRows = (tmpRowsMap.get(key) ?? []).filter((r) => r.id !== rowId)
      tmpRowsMap.set(key, newRows)
      setRows(tmpRowsMap)
    },
    [rows],
  )

  const sUpdateRowResourcesNumber = useCallback(
    (key: ReservationGroupUpsertGridRowGroupingInfo, rowId: string, selectedResourcesNumber: number) => {
      const tmpRowsMap = new Map(rows)
      const newRows = tmpRowsMap.get(key) ?? []

      const remainingAvailability = [...newRows].reduce((prev, curr) => {
        if (curr.id === rowId) return prev + selectedResourcesNumber
        return prev + curr.resourceNumber
      }, 0)

      tmpRowsMap.set(
        key,
        newRows.map((r) => {
          const newRow = { ...r }
          if (r.id === rowId) {
            newRow.resourceNumber = selectedResourcesNumber
          }
          newRow.type.availability = newRow.type.initialAvailability - remainingAvailability
          newRow.type.notAvailable = newRow.type.availability <= 0 || newRow.type.availability > newRow.type.maxAvailability
          return newRow
        }),
      )

      setRows(tmpRowsMap)
    },
    [rows],
  )

  const sUpdateRowOccupancy = useCallback(
    (key: ReservationGroupUpsertGridRowGroupingInfo, rowId: string, newOccupancy: ReservationGroupUpsertGridRowOccupancy, newPrice: number) => {
      const tmpRowsMap = new Map(rows)
      const newRows = (tmpRowsMap.get(key) ?? []).map((r) => {
        const newRow = { ...r }
        if (r.id === rowId) {
          newRow.occupancies = newOccupancy
          if (r.initialAveragePrice === r.averagePrice) {
            newRow.averagePrice = newPrice
            newRow.initialAveragePrice = newPrice
            newRow.initialTotalPrice = newPrice * newRow.nights
            newRow.totalPrice = newPrice * newRow.nights
          }
          const totalOccupancy = newOccupancy.adults + newOccupancy.children.filter((child) => !child.noOccupancy)?.reduce((prev, curr) => prev + curr.quantity, 0)
          newRow.type.occupancyError = newRow.type.maxOccupancy <= totalOccupancy
        }
        return newRow
      })

      tmpRowsMap.set(key, newRows)
      setRows(tmpRowsMap)
    },
    [rows],
  )

  const sUpdateRowTotalPrice = useCallback(
    (key: ReservationGroupUpsertGridRowGroupingInfo, rowId: string, newPrice: number) => {
      const tmpRowsMap = new Map(rows)
      const newRows = (tmpRowsMap.get(key) ?? []).map((r) => {
        const newRow = { ...r }
        if (r.id === rowId) {
          newRow.averagePrice = Math.ceil(newPrice / newRow.nights)
          newRow.totalPrice = newPrice
          newRow.shouldCalculatePrice = false
        }
        return newRow
      })

      tmpRowsMap.set(key, newRows)
      setRows(tmpRowsMap)
    },
    [rows],
  )

  const updateRowInitialPrice = useCallback(
    (key: ReservationGroupUpsertGridRowGroupingInfo, rowId: string, newPrice: number) => {
      const tmpRowsMap = new Map(rows)
      const newRows = (tmpRowsMap.get(key) ?? []).map((r) => {
        const newRow = { ...r }
        if (r.id === rowId) {
          const newAvgPrice = Math.round(newPrice / newRow.nights)
          newRow.averagePrice = newAvgPrice
          newRow.shouldCalculatePrice = false
          newRow.initialAveragePrice = newAvgPrice
          newRow.position = newPrice
          newRow.initialTotalPrice = newPrice
          newRow.totalPrice = newPrice
        }
        return newRow
      })

      tmpRowsMap.set(key, newRows)
      setRows(tmpRowsMap)
    },
    [rows],
  )

  const createEmptyRow = useCallback(
    (type: ReservationGroupUpsertGridRowType, groupingInfo: ReservationGroupUpsertGridRowGroupingInfo, initialAveragePrice: number): ReservationGroupUpsertGridRow => {
      return {
        id: uuid(),
        type: type,
        groupingInfo: groupingInfo,
        indexInTheGroup: rows.get(groupingInfo)?.length ?? 0,
        resourceNumber: 0,
        occupancies: {
          adults: type.maxAdults ?? type.occupancy,
          hideChildren: hideChildren,
          children:
            [...(childrenAgeRange ?? [])].reverse()?.map(({ child }) => {
              return {
                childId: child.id,
                label: localizeString(child.taxonomy.description),
                quantity: 0,
                noOccupancy: child.noOccupancy,
              }
            }) ?? [],
        },
        averagePrice: initialAveragePrice,
        initialAveragePrice: initialAveragePrice,
        initialTotalPrice: initialAveragePrice * nights,
        totalPrice: initialAveragePrice * nights,
        nights: nights,
        position: initialAveragePrice,
        filters: {
          adults: type.occupancy,
          chargeCategoryId: groupingInfo.chargeCategoryId,
          end: end,
          propertyId: propertyId,
          rateId: groupingInfo.rateId,
          resourceTypeId: groupingInfo.resourceTypeId,
          start: start,
          tenantId: tenantId,
        },
        shouldCalculatePrice: true,
      }
    },
    [rows, nights, childrenAgeRange, hideChildren],
  )

  useEffect(() => {
    if (reservationRates) {
      const tmpRowsMap: Map<ReservationGroupUpsertGridRowGroupingInfo, ReservationGroupUpsertGridRow[]> = new Map()
      for (const occupancy in reservationRates) {
        const query = reservationRates[occupancy]
        const tmpTypes: ReservationGroupUpsertGridRowType[] = []
        for (const res of query?.rateQuery?.resources ?? []) {
          if (tmpTypes.map((type) => type.resourceTypeId).includes(res.resourceType.id)) continue
          tmpTypes.push({
            resourceTypeId: res.resourceType.id,
            resourceTypeName: res.resourceType.code ?? localizeString(res.resourceType.customName) ?? localizeString(res.resourceType.name.description),
            availability: res.availability,
            initialAvailability: res.availability,
            maxAvailability: res.inventory,
            occupancy: res.resourceType.persons,
            maxOccupancy: res.resourceType.persons + res.resourceType.additionalPersons,
            occupancyError: false,
            notAvailable: res.availability <= 0 || res.availability > res.inventory,
            initialNotAvailable: res.availability <= 0 || res.availability > res.inventory,
            maxAdults: res.resourceType.maxAdults ?? undefined,
          })
        }

        const resourceTypeIds = resourceTypePerOccupancy[occupancy]
        for (const resourceTypeId of resourceTypeIds) {
          const rtExists = tmpTypes.find((rt) => {
            return rt.resourceTypeId === resourceTypeId
          })
          const rateId = query?.rateQuery.rates?.[0]?.rate?.id ?? '' // the rate is the same for all the resources because it is passed as filter
          const chargeCategoryId = query?.rateQuery.rates?.[0]?.resourceRates?.[0]?.chargeCategoryId ?? '' // the charge category is the same for all the resources because it is passed as filter

          let resourceRate = null
          for (const rate of query?.rateQuery?.rates ?? []) {
            for (const resourceRateToCheck of rate?.resourceRates ?? []) {
              const occupancyToCheck = resourceRateToCheck.resourceType.maxAdults ?? resourceRateToCheck.resourceType.persons

              if (occupancyToCheck === Number(occupancy)) {
                resourceRate = resourceRateToCheck
                break
              }
            }
            if (resourceRate) break
          }

          if (rtExists && resourceRate) {
            const averagePrice = Math.ceil(resourceRate.dailyPrice.reduce((prev, curr) => prev + curr.price, 0) / resourceRate.dailyPrice.length)
            const row = createEmptyRow(
              rtExists,
              {
                resourceTypeId: resourceTypeId,
                rateId,
                chargeCategoryId,
              },
              averagePrice,
            )
            const tmpRows = tmpRowsMap.get(row.groupingInfo) ?? []
            tmpRows.push(row)
            tmpRowsMap.set(row.groupingInfo, tmpRows)
          }
        }

        // reservationRates?.rateQuery.rates?.forEach(({ rate, resourceRates }) => {
        //   resourceRates?.forEach((resRate) => {
        //     const rtExists = tmpTypes.find((rt) => {
        //       return rt.resourceTypeId === resRate.resourceTypeId
        //     })
        //     const averagePrice = Math.ceil(resRate.dailyPrice.reduce((prev, curr) => prev + curr.price, 0) / resRate.dailyPrice.length)
        //     if (rtExists) {
        //       const row = createEmptyRow(
        //         rtExists,
        //         {
        //           resourceTypeId: resRate.resourceTypeId,
        //           rateId: rate.id,
        //           chargeCategoryId: resRate.chargeCategoryId,
        //         },
        //         averagePrice,
        //       )
        //       const tmpRows = tmpRowsMap.get(row.groupingInfo) ?? []
        //       tmpRows.push(row)
        //       tmpRowsMap.set(row.groupingInfo, tmpRows)
        //     }
        //   })
        // })
      }
      setRows(tmpRowsMap)
    }
  }, [reservationRates, nights])

  const removeRow = useCallback((groupingInfo: ReservationGroupUpsertGridRowGroupingInfo, rowId: string) => sRemoveRow(groupingInfo, rowId), [rows])

  const updateRowResourcesNumber = useCallback(
    (groupingInfo: ReservationGroupUpsertGridRowGroupingInfo, rowId: string, selectedResourcesNumber: number) => sUpdateRowResourcesNumber(groupingInfo, rowId, selectedResourcesNumber),
    [rows],
  )

  const updateRowOccupancy = useCallback(
    (groupingInfo: ReservationGroupUpsertGridRowGroupingInfo, rowId: string, newOccupancy: ReservationGroupUpsertGridRowOccupancy, newPrice: number) =>
      sUpdateRowOccupancy(groupingInfo, rowId, newOccupancy, newPrice),
    [rows],
  )

  const updateRowTotalPrice = useCallback((groupingInfo: ReservationGroupUpsertGridRowGroupingInfo, rowId: string, newPrice: number) => sUpdateRowTotalPrice(groupingInfo, rowId, newPrice), [rows])

  const createRow = useCallback(
    (groupingInfo: ReservationGroupUpsertGridRowGroupingInfo) => {
      const tmpGroupRows = rows.get(groupingInfo)
      if (tmpGroupRows && tmpGroupRows.length > 0) {
        sAddRow(groupingInfo, createEmptyRow({ ...tmpGroupRows[0].type, occupancyError: false }, groupingInfo, tmpGroupRows[0].position))
      }
    },
    [rows],
  )

  const updateTotalRowTotalPrice = useCallback(
    (initialPrice: number, newPrice: number) => {
      const delta = newPrice - initialPrice
      const sign = delta >= 0 ? 1 : -1
      let variationPercentage = newPrice / initialPrice
      variationPercentage = Math.abs(1 - variationPercentage)
      const updatedRows = new Map(rows)
      Array.from(rows.keys()).forEach((key) => {
        const rt = rows.get(key)
        if (rt) {
          const rtRows = rt.map((row) => {
            if (row.resourceNumber > 0) {
              const totalValue = Math.round(row.initialTotalPrice + row.initialTotalPrice * variationPercentage * sign)
              console.log('totalValue')
              console.log(totalValue)
              return {
                ...row,
                averagePrice: Math.round(totalValue / row.nights),
                totalPrice: totalValue,
              }
            }
            return row
          })
          updatedRows.set(key, rtRows)
        }
      })
      setRows(updatedRows)
    },
    [rows],
  )

  useEffect(() => {
    // if (localHideChildren !== hideChildren) {
    const tmpRowsMap = new Map(rows)
    Array.from(tmpRowsMap.keys()).forEach((key) => {
      tmpRowsMap.set(
        key,
        tmpRowsMap.get(key)?.map((r) => {
          return {
            ...r,
            occupancies: {
              ...r.occupancies,
              hideChildren: hideChildren,
            },
          }
        }) ?? [],
      )
    })
    setRows(tmpRowsMap)
    // }
  }, [/* rows, */ hideChildren])

  return { dataGridRows, updateRowResourcesNumber, updateRowOccupancy, updateRowTotalPrice, removeRow, createRow, updateTotalRowTotalPrice, updateRowInitialPrice }
}
